import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import { type SearchFlags } from '~/vendor/cx-web/domain-types';

import { featureFlags } from './index';

export { featureFlags };

export type FeatureFlagProviderProps = {
  featureToggleDefinitions: {
    description?: string;
    enabled: boolean;
    name: string;
  }[];
};

export type FeatureFlagProviderData = FeatureFlagProviderProps;

export const FeatureFlagContext = createContext<FeatureFlagProviderData>(undefined as any as FeatureFlagProviderData);
FeatureFlagContext.displayName = 'FeatureFlagContext';

export const FeatureFlagProvider = ({
  children,
  featureToggleDefinitions,
  ...props
}: PropsWithChildren<FeatureFlagProviderData>) => {
  const value = useMemo(
    () => ({
      featureToggleDefinitions,
    }),
    [featureToggleDefinitions],
  );

  return (
    <FeatureFlagContext.Provider value={value} {...props}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagContext = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlagContext can only be used inside of FeatureFlagProvider');
  }

  return context;
};

/**
 * Looks up a toggle by name. Prefer {@link useFeatureFlag}, which constrains the name to a
 * `featureFlags` registry key; this variant exists for names computed at runtime (e.g. a per-repub flag
 * name), which that type cannot express.
 */
export function useFeatureFlagByName(name: string) {
  const context = useFeatureFlagContext();

  const featureToggle = context.featureToggleDefinitions.find(featureToggle => featureToggle.name === name);

  return featureToggle?.enabled ?? false;
}

export function useFeatureFlag<K extends keyof typeof featureFlags>(name: (typeof featureFlags)[K]) {
  return useFeatureFlagByName(name);
}

function isFeatureFlagEnabled(
  featureToggleDefinitions: FeatureFlagProviderProps['featureToggleDefinitions'],
  name: string,
): boolean {
  return featureToggleDefinitions.some(featureToggle => featureToggle.name === name && featureToggle.enabled);
}

export function getSearchFlags(
  featureToggleDefinitions: FeatureFlagProviderProps['featureToggleDefinitions'],
): SearchFlags {
  return {
    allWnba: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__ALL_WNBA),
    internalSearchTools: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS),
    last10PersistentSort: isFeatureFlagEnabled(
      featureToggleDefinitions,
      featureFlags.SUBS__PLUS__LAST_10_PERSISTENT_SORT,
    ),
    lineReduction: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__LINE_REDUCTION),
    newCategoryGroups: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__NEW_CATEGORY_GROUPS),
    soccerBettingSplits: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__SOCCER_BETTING_SPLITS),
    soccerEplOnly: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__SOCCER_EPL_ONLY),
    soccerTrends: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__SOCCER_TRENDS),
    worldCup26: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__WORLD_CUP26),
  };
}
